import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import Button from '../components/Button'

const Onboarding = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [direction, setDirection] = useState(0) // 1 for forward, -1 for backward
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: '',
    phone: '+91 8884012211',
    email: '',
  })

  const [errors, setErrors] = useState({})

  // Validation functions
  const validateName = (name) => {
    if (!name.trim()) return 'Name is required'
    if (name.trim().length < 2) return 'Name must be at least 2 characters'
    return ''
  }

  const validatePhone = (phone) => {
    if (!phone.trim()) return 'Phone number is required'
    const phoneRegex = /^[\d\s\-+()]{10,}$/
    if (!phoneRegex.test(phone)) return 'Please enter a valid phone number'
    return ''
  }

  const validateEmail = (email) => {
    if (!email.trim()) return 'Email is required'
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) return 'Please enter a valid email address'
    return ''
  }

  const validateAge = (age) => {
    if (!age.trim()) return 'Age is required'
    const ageNum = parseInt(age)
    if (isNaN(ageNum)) return 'Please enter a valid age'
    if (ageNum < 13 || ageNum > 120) return 'Please enter an age between 13 and 120'
    return ''
  }

  const validateGender = (gender) => {
    if (!gender.trim()) return 'Please select your gender'
    return ''
  }

  // Step configuration
  const steps = [
    {
      id: 'welcome',
      title: "Thank you for taking your first step towards better wellness",
      subtitle: "Let's personalize your wellness journey together",
      type: 'welcome',
      icon: '🌟',
      validate: () => '', // No validation needed for welcome screen
    },
    {
      id: 'name',
      title: "What's your name?",
      subtitle: "Let's start with the basics",
      field: 'name',
      placeholder: 'Enter your full name',
      type: 'text',
      icon: '👋',
      validate: validateName,
    },
    {
      id: 'age',
      title: "What's your age?",
      subtitle: 'This helps us personalize your wellness plan',
      field: 'age',
      placeholder: 'Enter your age',
      type: 'number',
      icon: '🎂',
      validate: validateAge,
    },
    {
      id: 'gender',
      title: "What's your gender?",
      subtitle: 'Help us tailor your experience',
      field: 'gender',
      type: 'select',
      icon: '👤',
      validate: validateGender,
      options: [
        { value: '', label: 'Select your gender' },
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'non-binary', label: 'Non-binary' },
        { value: 'prefer-not-to-say', label: 'Prefer not to say' },
      ],
    },
    {
      id: 'phone',
      title: "What's your phone number?",
      subtitle: "We'll use this to keep you updated",
      field: 'phone',
      placeholder: '+91 8884012211',
      type: 'tel',
      icon: '📱',
      validate: validatePhone,
    },
    {
      id: 'email',
      title: "What's your email?",
      subtitle: 'Your wellness journey updates will be sent here',
      field: 'email',
      placeholder: 'your.email@example.com',
      type: 'email',
      icon: '📧',
      validate: validateEmail,
    },
  ]

  const currentStepData = steps[currentStep]

  const handleInputChange = (e) => {
    const { value } = e.target
    setFormData((prev) => ({
      ...prev,
      [currentStepData.field]: value,
    }))
    // Clear error when user starts typing
    if (errors[currentStepData.field]) {
      setErrors((prev) => ({
        ...prev,
        [currentStepData.field]: '',
      }))
    }
  }

  const handleNext = () => {
    // Skip validation for welcome screen
    if (currentStepData.type !== 'welcome') {
      // Validate current field
      const error = currentStepData.validate(formData[currentStepData.field])
      
      if (error) {
        setErrors((prev) => ({
          ...prev,
          [currentStepData.field]: error,
        }))
        return
      }
    }

    if (currentStep < steps.length - 1) {
      setDirection(1)
      setCurrentStep((prev) => prev + 1)
    } else {
      // Last step - submit the form
      handleSubmit()
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setDirection(-1)
      setCurrentStep((prev) => prev - 1)
    }
  }

  const handleSubmit = () => {
    console.log('Basic onboarding completed:', formData)
    // Store basic info and navigate to wellness preferences
    localStorage.setItem('basicOnboardingData', JSON.stringify(formData))
    navigate('/onboarding-wellness')
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleNext()
    }
  }

  // Animation variants
  const pageVariants = {
    initial: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.8,
    }),
    animate: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.43, 0.13, 0.23, 0.96],
      },
    },
    exit: (direction) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.3,
      },
    }),
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-purple-50 to-pink-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-2xl">
        {/* Logo */}
        <div className="mb-6 flex justify-center">
          <img 
            src="/logo.png" 
            alt="Wellness Loop Logo" 
            className="h-16 w-auto"
          />
        </div>
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600">
              Step {currentStep + 1} of {steps.length}
            </span>
            <span className="text-sm font-medium text-primary-600">
              {Math.round(((currentStep + 1) / steps.length) * 100)}%
            </span>
          </div>
          <div className="h-2 bg-white/50 rounded-full overflow-hidden backdrop-blur-sm">
            <motion.div
              className="h-full bg-gradient-to-r from-primary-600 to-purple-600 origin-left"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: (currentStep + 1) / steps.length }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
        </div>

        {/* Main card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-2xl p-8 md:p-12"
        >
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentStep}
              custom={direction}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="space-y-8"
            >
              {/* Icon */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ 
                  type: 'spring',
                  stiffness: 200,
                  damping: 15,
                  delay: 0.2,
                }}
                className="text-7xl text-center"
              >
                {currentStepData.icon}
              </motion.div>

              {/* Title and subtitle */}
              <div className="text-center space-y-2">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                  {currentStepData.title}
                </h2>
                <p className="text-lg text-gray-600">
                  {currentStepData.subtitle}
                </p>
              </div>

              {/* Input field */}
              {currentStepData.type !== 'welcome' && (
                <div className="space-y-2">
                  {currentStepData.type === 'select' ? (
                    <select
                      value={formData[currentStepData.field]}
                      onChange={handleInputChange}
                      autoFocus
                      className={`w-full px-6 py-4 text-lg border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${
                        errors[currentStepData.field]
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      {currentStepData.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={currentStepData.type}
                      value={formData[currentStepData.field]}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder={currentStepData.placeholder}
                      autoFocus
                      className={`w-full px-6 py-4 text-lg border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${
                        errors[currentStepData.field]
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-200 bg-gray-50'
                      }`}
                    />
                  )}
                  <AnimatePresence>
                    {errors[currentStepData.field] && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="text-red-600 text-sm px-2"
                      >
                        {errors[currentStepData.field]}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Navigation buttons */}
              <div className="flex gap-4 pt-4">
                {currentStep > 0 && (
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    className="flex-1 py-4 text-lg"
                  >
                    Back
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={handleNext}
                  className={`py-4 text-lg ${currentStep === 0 ? 'w-full' : 'flex-1'}`}
                >
                  {currentStep === 0 ? 'Start now' : currentStep === steps.length - 1 ? 'Complete' : 'Continue'}
                </Button>
              </div>

              {/* Step indicators */}
              <div className="flex justify-center gap-2 pt-4">
                {steps.map((_, index) => (
                  <motion.div
                    key={index}
                    className={`h-2 rounded-full transition-all ${
                      index === currentStep
                        ? 'w-8 bg-primary-600'
                        : index < currentStep
                        ? 'w-2 bg-primary-400'
                        : 'w-2 bg-gray-300'
                    }`}
                    initial={false}
                    animate={{
                      scale: index === currentStep ? 1.2 : 1,
                    }}
                  />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Skip option */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-6"
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-600 hover:text-gray-900 transition-colors text-sm"
          >
            Skip for now
          </button>
        </motion.div>
      </div>
    </div>
  )
}

export default Onboarding

